import { rem } from "@/styles/utils"
import {
  black,
  device_px,
  gray,
  primary,
  white,
  yellow
} from "@/styles/variable"
import * as Dialog from "@radix-ui/react-dialog"
import styled from "styled-components"

const { MAX_1279, MAX_767 } = device_px

export const StyledDialog = {
  Overlay: styled(Dialog.Overlay)`
    background-color: ${black["0060"]};
    position: fixed;
    top: ${rem(72)};
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    backdrop-filter: blur(5px);
    transition: opacity 0.3s ease-in-out;

    &[data-state="open"] {
      opacity: 1;
      pointer-events: auto;
    }

    &[data-state="closed"] {
      opacity: 0;
      pointer-events: none;
    }

    @media screen and (max-width: ${MAX_1279}) {
      top: ${rem(67)};
    }

    @media screen and (max-width: ${MAX_767}) {
      top: ${rem(56)};
    }
  `,
  Content: styled(Dialog.Content)`
    top: ${rem(72)};
    max-height: calc(100dvh - 72px - 30px - 20px);
    max-width: min(100%, calc(100vw - 60px));
    width: 100%;

    @media only screen and (min-width: 1440px) {
      max-width: min(1426px, calc(100vw - 60px));
    }

    @media only screen and (max-width: ${MAX_1279}) {
      max-height: calc(100dvh - 67px - 20px - 20px);
      top: ${rem(67)};
      margin-top: 20px;
      max-width: min(100%, calc(100vw - 40px));
    }

    @media only screen and (max-width: ${MAX_767}) {
      max-height: calc(100dvh - 56px - 20px - 20px);
      top: ${rem(56)};
      max-width: min(100%, calc(100vw - 40px));
    }

    background-color: ${gray["2"]};
    border: 1px solid ${white["20"]};
    border-radius: 12px;
    left: 50%;
    transform: translateX(-50%);
    padding: 30px;
    display: flex;
    flex-direction: column;
    position: fixed;
    z-index: 1000;
    animation: popupScale 150ms cubic-bezier(0.15, 1, 0.3, 1);
    margin-top: 30px;
    @keyframes popupScale {
      from {
        opacity: 0;
        transform: translateX(-50%) scale(0.95);
      }
      to {
        opacity: 1;
        transform: translateX(-50%) scale(1);
      }
    }

    @media only screen and (orientation: landscape) and (max-height: 571px) {
      min-height: 485px;
    }

    @media only screen and (orientation: portrait) and (max-witdh: 768px) {
      min-height: 485px;
    }

    @media only screen and (orientation: landscape) and (max-height: 571px) {
      max-width: unset;
    }

    @media only screen and (orientation: portrait) and (max-height: 571px) {
      min-height: unset;
    }
  `,
  Header: styled.div`
    margin-bottom: ${rem(30)};
  `,
  Body: styled.div<{ $showScrollbar: boolean }>`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: auto;
    max-height: min(1220px, calc(100dvh - 72px - 30px - 90px));
    padding-bottom: 6px;
    overflow-x: auto;
    display: inline-block;

    &::-webkit-scrollbar {
      width: 8px;
      height: 8px;
      display: block;
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${({ $showScrollbar }) =>
        $showScrollbar ? gray.FF20 : "transparent"};
      border-radius: ${rem(4)};
      transition: background-color 0.3s ease-in-out;
    }

    &::-webkit-scrollbar-corner {
      display: none;
    }

    @media only screen and (max-width: 1920px) {
      &::-webkit-scrollbar-thumb {
        background-color: ${gray.FF20};
      }
    }
    @media only screen and (max-width: ${MAX_767}) {
      &::-webkit-scrollbar {
        width: 4px;
        height: 4px;
      }
    }

    @media only screen and (min-width: 768px) {
      & {
        padding-right: 6px;
      }
    }

    @media only screen and (max-width: 768px) {
      & {
        padding-right: 3px;
      }
    }
  `,
  CloseButton: styled(Dialog.Close)`
    height: ${rem(30)};
    width: ${rem(30)};
    padding: ${rem(5)};
    display: flex;
    justify-content: center;
    align-items: center;
    color: ${yellow.FF};
    float: right;
    background: transparent;
    border: none;
    outline: none;
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 100%;
    box-sizing: content-box;
    position: relative;
    top: -5px;

    > * {
      flex-shrink: 0;
    }

    @media only screen and (max-width: ${MAX_1279}) {
      height: ${rem(24)};
      width: ${rem(24)};
      padding: ${rem(4)};
      top: -4px;
      svg {
        height: ${rem(16)};
      }
    }
    @media only screen and (max-width: 400px) {
      right: -5px;
    }
  `
}

export const Title = styled.div<{ $isKO: boolean }>`
  font-size: ${rem(24)};
  width: 200px;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: ${rem(-0.72)};
  color: ${primary.white};
  display: inline-block;
  text-align: center;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  margin: 0;
  @media only screen and (max-width: 400px) {
    left: 50%;
  }
  @media only screen and (max-width: 360px) {
    font-size: ${({ $isKO }) => ($isKO ? "24px" : "20px")};
  }
`

export const DetailText = styled.div`
  font-size: ${rem(10)};
  font-weight: 500;
  line-height: 165%; /* 16.5px */
  letter-spacing: -0.3px;
  margin-top: 2px;
  color: ${white[80]};
  @media only screen and (orientation: landscape) and (max-height: 571px) {
    display: none;
  }
  @media screen and (orientation: landscape) and (min-height: 460px) and (max-height: 571px) {
    display: flex;
  }
`

export const ScrollDetailText = styled.div`
  font-size: ${rem(10)};
  font-weight: 500;
  line-height: 165%; /* 16.5px */
  letter-spacing: -0.3px;
  margin-top: 2px;
  color: ${white[80]};
  display: none;
  @media only screen and (orientation: landscape) and (max-height: 571px) {
    display: flex;
  }
  @media screen and (orientation: landscape) and (min-height: 460px) and (max-height: 571px) {
    display: none;
  }
`
